import { cn } from "@/lib/utils";
import QrCodeMark from "../qrCodeMark/qrCodeMark.component";

// The small picture under each step (the preview's `.fs-v` contents).
export type FlowStepVisual =
  // Table-tent QR code.
  | { kind: "qr" }
  // Visit stamps, `filled` of `total`, with a count line.
  | { kind: "stamps"; filled: number; total: number; label: string }
  // Time slots with one highlighted.
  | { kind: "slots"; slots: string[]; active: string }
  // Chat bubble: incoming (white) or outgoing (green).
  | { kind: "bubble"; text: string; isOutgoing?: boolean }
  // A big figure with a label, e.g. "₹25,000 bill".
  | { kind: "kpi"; value: string; label: string }
  // Coloured chip.
  | { kind: "chip"; text: string; tone: "mint" | "lilac" | "sun" };

export interface FlowStep {
  title: string;
  text: string;
  visual: FlowStepVisual;
}

interface FlowStepsProps {
  ariaLabel: string;
  steps: FlowStep[];
}

// Step-number colours by position (the preview's nth-child rules).
const numberTones = [
  "bg-primary",
  "bg-tone-lilac-ink",
  "bg-tone-mint-ink",
  "bg-tone-sun-ink",
];

const chipTones = {
  mint: "bg-tone-mint text-tone-mint-ink",
  lilac: "bg-tone-lilac text-tone-lilac-ink",
  sun: "bg-tone-sun text-tone-sun-ink",
};

const visualFor = (visual: FlowStepVisual) => {
  switch (visual.kind) {
    case "qr":
      return <QrCodeMark size="xs" className="w-fit border-2 border-line" />;
    case "stamps":
      return (
        <>
          <span aria-hidden="true" className="flex gap-1.5">
            {Array.from({ length: visual.total }, (_, index) => (
              <i
                key={index}
                className={cn(
                  "size-6 rounded-full border-[3px]",
                  index < visual.filled
                    ? "border-solid border-primary bg-primary"
                    : "border-dashed border-line",
                )}
              />
            ))}
          </span>
          <small className="text-[13.5px] text-ink-muted">{visual.label}</small>
        </>
      );
    case "slots":
      return (
        <span aria-hidden="true" className="flex w-full gap-1.5">
          {visual.slots.map((slot) => (
            <i
              key={slot}
              className={cn(
                "flex-1 rounded-[10px] py-[7px] text-center text-[13px] font-bold not-italic",
                slot === visual.active
                  ? "bg-primary text-white"
                  : "bg-cream text-ink",
              )}
            >
              {slot}
            </i>
          ))}
        </span>
      );
    case "bubble":
      return (
        <span
          className={cn(
            "border px-2.5 py-1.5 text-[13px] leading-snug",
            visual.isOutgoing
              ? "rounded-[12px_12px_2px_12px] border-tone-mint bg-tone-mint"
              : "rounded-[12px_12px_12px_2px] border-line bg-white",
          )}
        >
          {visual.text}
        </span>
      );
    case "kpi":
      return (
        <span className="rounded-[10px] bg-cream px-2.5 py-1.5 text-[13px]">
          <b className="text-[18px] font-bold">{visual.value}</b> {visual.label}
        </span>
      );
    case "chip":
      return (
        <span
          className={cn(
            "rounded-full px-2.5 py-1 text-[12.5px] font-bold",
            chipTones[visual.tone],
          )}
        >
          {visual.text}
        </span>
      );
  }
};

// "From the table to the next visit" - the preview's `.fs` strip: numbered
// white step cards, each with a small picture of what happens, joined by
// dashed connectors on wide screens. Four steps sit in one row; six sit in
// two rows of three.
const FlowSteps = ({ ariaLabel, steps }: FlowStepsProps) => {
  const isSix = steps.length > 4;
  return (
    <figure role="group" aria-label={ariaLabel} className="m-0">
      <ol
        className={cn(
          "grid gap-4",
          isSix
            ? "md:grid-cols-2 xl:grid-cols-3"
            : "sm:grid-cols-2 lg:grid-cols-4",
        )}
      >
        {steps.map((step, index) => {
          const isRowEnd = isSix
            ? (index + 1) % 3 === 0
            : index === steps.length - 1;
          return (
            <li
              key={step.title}
              className="relative grid content-start gap-1.5 rounded-[22px] bg-white p-[18px] shadow-card"
            >
              <span
                aria-hidden="true"
                className={cn(
                  "grid size-[30px] place-items-center rounded-full text-[14px] font-extrabold text-white",
                  numberTones[index % numberTones.length],
                )}
              >
                {index + 1}
              </span>
              <b className="text-[16px] font-bold">{step.title}</b>
              <small className="text-[13.5px] text-ink-muted">
                {step.text}
              </small>
              <div className="mt-2 flex min-h-11 flex-wrap items-center gap-1.5">
                {visualFor(step.visual)}
              </div>
              {!isRowEnd && index < steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute top-[30px] -right-[17px] hidden h-[3px] w-[18px] bg-dash-primary",
                    isSix ? "xl:block" : "lg:block",
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </figure>
  );
};

export default FlowSteps;
